// Canonical milestones for the Paw-Some Reports admin dashboard.
// Treated as TRACKED milestones (not strictly linear) because Creative Draft
// completion and Waiting-for-Photos may overlap.

export const MILESTONES = [
  { key: "order_received",      label: "Order Received" },
  { key: "creative_draft",      label: "Creative Draft Complete" },
  { key: "waiting_for_photos",  label: "Waiting for Photos" },
  { key: "photos_received",     label: "Photos Received" },
  { key: "human_review",        label: "Ready for Human Review" },
  { key: "approved_assembly",   label: "Approved for Assembly" },
  { key: "report_complete",     label: "Report Complete" },
  { key: "ready_to_send",       label: "Ready to Send" },
  { key: "delivered",           label: "Delivered" },
];

const EVENT_TO_MILESTONE = {
  "Order Created":                 "order_received",
  "Creative AI Completed":         "creative_draft",
  "Creative AI Retry Completed":   "creative_draft",
  "Creative AI Regenerated":       "creative_draft",
  "Customer Photos Received":      "photos_received",
  "Creative Approved":             "approved_assembly",
  "Quality Control Approved":      "ready_to_send",
  "Final Report Ready to Send":    "ready_to_send",
  "Report Rendered":               "report_complete",
  "Final Report Delivered":        "delivered",
  "Report Delivered":              "delivered",
  "Delivery Confirmed":            "delivered",
};

export function computeMilestones(order, events = []) {
  const completed = new Set();

  if (order?.created_at || order?.order_number) completed.add("order_received");

  for (const e of events) {
    const k = EVENT_TO_MILESTONE[e.event_type];
    if (k) completed.add(k);
  }

  if (order?.photo_upload_completed_at) completed.add("photos_received");

  const reports = order?.reports || [];
  for (const r of reports) {
    if (r.creative_output) completed.add("creative_draft");
    if (r.approved_package) completed.add("approved_assembly");
    if (r.final_html_path) completed.add("report_complete");
    if (r.report_status === "Ready to Send" || r.report_status === "Delivery Failed") {
      completed.add("ready_to_send");
    }
    if (r.report_status === "Completed" || r.delivery?.delivery_status === "Sent") {
      completed.add("delivered");
    }
  }

  const status = order?.production_status || "";

  let currentKey = null;
  if (status === "Completed" || status === "Delivered") {
    completed.add("delivered");
    currentKey = "delivered";
  } else if (status === "Ready to Send") {
    completed.add("ready_to_send");
    currentKey = "ready_to_send";
  } else if (status === "Delivery Failed") {
    completed.add("ready_to_send");
    currentKey = "ready_to_send";
  } else if (status === "Production AI Processing") {
    currentKey = "approved_assembly";
    completed.add("approved_assembly");
  } else if (status === "Ready for Quality Control" || status === "Report Rendered") {
    currentKey = "report_complete";
    completed.add("report_complete");
  } else if (status === "Waiting for Creative Review" || status === "Ready for Human Review") {
    currentKey = "human_review";
  } else if (status === "Photo Review") {
    currentKey = "photos_received";
    completed.add("photos_received");
  } else if (status === "Waiting for Photos") {
    currentKey = "waiting_for_photos";
  } else if (status === "Creative AI Processing" || status === "Creative AI Failed" || status === "Manual Review Required") {
    currentKey = "creative_draft";
  } else if (status === "Payment Pending") {
    currentKey = "order_received";
  } else {
    const idx = MILESTONES.findIndex((m) => !completed.has(m.key));
    currentKey = idx >= 0 ? MILESTONES[idx].key : "delivered";
  }
  return { completed, currentKey };
}

export function statusBadgeLabel(order) {
  const s = order?.production_status || "Payment Pending";
  const map = {
    "Payment Pending":                 "Order Received",
    "Creative AI Processing":          "Drafting Creative",
    "Creative AI Failed":              "Creative Retry",
    "Manual Review Required":          "Manual Review",
    "Waiting for Photos":              "Waiting for Photos",
    "Photo Review":                    "Photos Received",
    "Waiting for Creative Review":     "Ready for Human Review",
    "Production AI Processing":        "Approved for Assembly",
    "Ready for Quality Control":       "Report Complete",
    "Report Rendered":                 "Report Complete",
    "Ready to Send":                   "Ready to Send",
    "Delivery Failed":                 "Delivery Failed",
    "Manual Delivery Review":          "Manual Delivery Review",
    "Completed":                       "Delivered",
    "Delivered":                       "Delivered",
    "On Hold":                         "On Hold",
    "Photo Needs Attention":           "Photo Needs Attention",
  };
  return map[s] || s;
}
